import React, { useEffect, useMemo } from 'react';
import { Grid } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { EEhsStatus, EEhsType, IBuilding, IEhs, IUser } from '@relab/alstom-ptw-shared';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { ActionType, RootState, ServerRequestStatus } from '../../redux/types';
import { appState, appStateServerRequests } from '../../redux/selectors';
import { ehsCreateAction, resetServerRequestAction } from '../../redux/actions';
import { creationValidationSchema, headlineValidationSchema } from './ehsValidationSchemas';
import { EhsHeadlineComponent, EhsPhaseCreation, InstructionsModal } from './ehsFormComponents';
import { useTranslation } from 'react-i18next';

interface EhsCreateComponentProps {
  ehsCreateAction: typeof ehsCreateAction;
  resetServerRequestAction: typeof resetServerRequestAction;
  createEhsServerRequest?: ServerRequestStatus;
  onCancel: () => void;
  onComplete: (ehs: IEhs) => void;
  buildings: IBuilding[];
  ehsType: EEhsType;
  user: IUser;
}

const PEhsCreateComponent: React.FC<EhsCreateComponentProps> = ({
  ehsCreateAction,
  resetServerRequestAction,
  onCancel,
  onComplete,
  createEhsServerRequest,
  buildings,
  ehsType,
  user,
}) => {
  useEffect(() => {
    if (createEhsServerRequest && createEhsServerRequest.success) {
      if (onComplete) {
        resetServerRequestAction(ActionType.EHS_CREATE);
        onComplete(createEhsServerRequest?.payload?.data);
      }
    }
  }, [createEhsServerRequest, onComplete, resetServerRequestAction]);

  const { t } = useTranslation();

  const transHeadlineValidationSchema = useMemo(() => headlineValidationSchema(t), [t]);
  const transCreationValidationSchema = useMemo(() => creationValidationSchema(t), [t]);

  const validationSchema = Yup.object().shape({
    ...transHeadlineValidationSchema.fields,
    ...transCreationValidationSchema.fields,
  });

  const initialValues: Partial<IEhs> = useMemo(() => {
    return {
      name: '',
      status: EEhsStatus.NUOVO,
      description: '',
      attivitaAltoRischio: [],
      building: '',
      startDate: undefined,
      startHour: undefined,
      endHour: undefined,
      internalExternal: undefined,
      internalEnte: '',
      externalDuvriNum: '',
      externalDuvriDate: undefined,
      nominativoResponsabile: '',
      nominativoSorveglianteAntincendio: '',
      ispezioneArea: undefined,
      dpi: [],
      dpiOther: '',
      estinguenti: [],
      estinguentiOther: '',
      misureEControlli: [],
      evidenzeFormazione: '',
      attachments: [],
      execMembers: [],
      approvazioneDate: undefined,
      approvazioneTime: undefined,
      supervisoreName: '',
      supervisoreSign: '',
      referenteAreaName: '',
      referenteAreaSign: '',
      referenteAttivitaName: '',
      referenteAttivitaSign: '',
      approvazioneLavoriACaldo: undefined,
      ispezioni: [],
      // fineLavoriTime: ,
      note: '',
      riconsegnaName: '',
      riconsegnaSign: '',
      prendeInConsegnaName: '',
      prendeInConsegnaSign: '',
      responsabileAltoRischioName: '',
      responsabileAltoRischioSign: '',
      elettricoTensioneAssente: false,
      idraulicoTensioneAssente: false,
      pneumaticoTensioneAssente: false,
      ossigenoValorePrima: '',
      ossigenoValoreDurante: '',
      ossigenoValoreTermine: '',
      monossidoValorePrima: '',
      monossidoValoreDurante: '',
      monossidoValoreTermine: '',
      metanoValorePrima: '',
      metanoValoreDurante: '',
      metanoValoreTermine: '',
      idrogenoValorePrima: '',
      idrogenoValoreDurante: '',
      idrogenoValoreTermine: '',
      imbracatureSiNo: '',
      imbracatureDateLast: '',
      matricolaAnalizzatore: '',
      scadenzaCalibrazione: undefined,
      formazioneUtilizzo: '',
      scadenzaGuantiDielettrici: undefined,
    };
  }, []);

  const onSubmit = (values: Partial<IEhs>) => {
    const {
      ossigenoValorePrima,
      ossigenoValoreDurante,
      ossigenoValoreTermine,
      monossidoValorePrima,
      monossidoValoreDurante,
      monossidoValoreTermine,
      metanoValorePrima,
      metanoValoreDurante,
      metanoValoreTermine,
      idrogenoValorePrima,
      idrogenoValoreDurante,
      idrogenoValoreTermine,
      ...otherValues
    } = values;

    const newEhs: Partial<IEhs> = {
      ...otherValues,
      ossigenoValorePrima:
        typeof ossigenoValorePrima === 'number' ? ossigenoValorePrima : parseFloat(ossigenoValorePrima),
      ossigenoValoreDurante:
        typeof ossigenoValoreDurante === 'number' ? ossigenoValoreDurante : parseFloat(ossigenoValoreDurante),
      ossigenoValoreTermine:
        typeof ossigenoValoreTermine === 'number' ? ossigenoValoreTermine : parseFloat(ossigenoValoreTermine),
      monossidoValorePrima:
        typeof monossidoValorePrima === 'number' ? monossidoValorePrima : parseFloat(monossidoValorePrima),
      monossidoValoreDurante:
        typeof monossidoValoreDurante === 'number' ? monossidoValoreDurante : parseFloat(monossidoValoreDurante),
      monossidoValoreTermine:
        typeof monossidoValoreTermine === 'number' ? monossidoValoreTermine : parseFloat(monossidoValoreTermine),
      metanoValorePrima: typeof metanoValorePrima === 'number' ? metanoValorePrima : parseFloat(metanoValorePrima),
      metanoValoreDurante:
        typeof metanoValoreDurante === 'number' ? metanoValoreDurante : parseFloat(metanoValoreDurante),
      metanoValoreTermine:
        typeof metanoValoreTermine === 'number' ? metanoValoreTermine : parseFloat(metanoValoreTermine),
      idrogenoValorePrima:
        typeof idrogenoValorePrima === 'number' ? idrogenoValorePrima : parseFloat(idrogenoValorePrima),
      idrogenoValoreDurante:
        typeof idrogenoValoreDurante === 'number' ? idrogenoValoreDurante : parseFloat(idrogenoValoreDurante),
      idrogenoValoreTermine:
        typeof idrogenoValoreTermine === 'number' ? idrogenoValoreTermine : parseFloat(idrogenoValoreTermine),
    };

    ehsCreateAction(newEhs, { attachments: newEhs.attachments });
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ values }) => (
        <Form className={'ui form'}>
          <EhsHeadlineComponent onCancel={onCancel} ehs={values as IEhs} user={user} />
          <Grid padded={'horizontally'} relaxed>
            <Grid.Row stretched>
              <Grid.Column>
                <EhsPhaseCreation buildings={buildings} ehsType={ehsType} userRoles={user.userRoles} />
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <InstructionsModal />
        </Form>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => ({
  user: appState(state).user,
  createEhsServerRequest: appStateServerRequests(state, ActionType.EHS_CREATE),
});

export const EhsCreateComponent = connect(mapStateToProps, {
  ehsCreateAction,
  resetServerRequestAction,
})(PEhsCreateComponent);
